import { fetchData } from "app/services/apiService";
import { formattaData } from "app/utility/dataOra";

/**
 * Normalizza OperationResult<T> dal BE (camelCase).
 * - NON fa reject
 * - Se isSuccess=false, aggiunge una sezione ui:
 *   ui.errorRows -> array righe {progressivo, messaggio, codEsito, tipoErrore}
 *   ui.userMessage -> stringa pronta per NotificationManager
 *   ui.detailsJson -> json completo da mostrare nel <details>
 */
const handleOperationResult = (opResult, fallbackMessage = "Operazione non riuscita") => {
    const safe = opResult && typeof opResult === "object" ? opResult : {};
    const isSuccess = safe?.isSuccess === true;

    // data: nel tuo scenario esiste sempre un oggetto quando arriva dal BE.
    // In casi anomali (es. parsing / rete) lo normalizzo comunque a {}
    const data = safe?.data && typeof safe.data === "object" ? safe.data : {};

    // errori: nel tuo caso sono dentro data.elencoErroriRicette (o cambia qui se il nome è diverso)
    const errori = Array.isArray(data?.elencoErroriRicette) ? data.elencoErroriRicette : [];

    // mappo TUTTI gli errori
    const errorRows = errori.map((e) => {
        const progressivo =
            e?.progPresc === null || e?.progPresc === undefined ? "" : String(e.progPresc);

        return {
            progressivo,
            messaggio: String(e?.esito ?? "").trim(),
            codEsito: String(e?.codEsito ?? "").trim(),
            tipoErrore: String(e?.tipoErrore ?? "").trim(),
        };
    });

    const messageFromBe = String(safe?.message ?? "").trim();
    const baseMessage = messageFromBe || fallbackMessage;

    // userMessage: unisco tutte le righe (se presenti) altrimenti baseMessage
    // (se progressivo è vuoto, mostro solo messaggio)
    const userMessage = errorRows.length
        ? errorRows
            .map((r) => (r.progressivo ? `Progressivo: ${r.progressivo} - ${r.messaggio || baseMessage}` : (r.messaggio || baseMessage)))
            .join("\n")
        : baseMessage;

    return {
        ...safe,
        isSuccess,
        message: messageFromBe || "", // lascio il BE message "puro" (può essere anche vuoto)
        data,
        ui: {
            errorRows,
            userMessage,
            detailsJson: data, // qui c’è TUTTO: elencoRicette, elencoErroriRicette, elencoComunicazioni, ecc.
            baseMessage,       // fallback che stai usando lato UI se ti serve
        },
    };
};

/**
 * Wrapper comune: chiama fetchData e applica handleOperationResult
 * - NON fa reject
 * - in caso di errore "tecnico" (rete/parsing) ritorna un OperationResult isSuccess=false
 */
const postOp = (apiUrl, request, fallbackMessage) => {
    return (dispatch) => {
        return dispatch(fetchData(apiUrl, request, null, "POST"))
            .then((opResult) => handleOperationResult(opResult, fallbackMessage))
            .catch((err) => {
                const msg = String(err?.message ?? "").trim() || fallbackMessage || "Errore imprevisto";

                // "OperationResult" finto ma compatibile col tuo flusso
                return handleOperationResult(
                    {
                        isSuccess: false,
                        message: msg,
                        data: {
                            elencoErroriRicette: [
                                {
                                    codEsito: "",
                                    esito: msg,
                                    progPresc: "",
                                    tipoErrore: "Tecnico",
                                },
                            ],
                        },
                    },
                    msg
                );
            });
    };
};

// --- i tuoi metodi ---
export const visualizzaRicetta = (request) =>
    postOp("Nre/VisualizzaRicetta", request, "Errore durante la visualizzazione della ricetta");

export const rilasciaRicetta = (request) =>
    postOp("Nre/RilasciaRicetta", request, "Errore durante il rilascio della ricetta");

export const annullaErogato = (request) =>
    postOp("Nre/AnnullaErogato", request, "Errore durante l'annullamento dell'erogato");

export const ricercaErogato = (request) =>
    postOp("Nre/Ricerca", request, "Errore durante la ricerca della ricetta");

// helper “dati” se vuoi usarlo senza JSX
export const getErroreRicettaUI = (opResult) => {
    return {
        userMessage: String(opResult?.ui?.userMessage ?? opResult?.message ?? ""),
        rows: Array.isArray(opResult?.ui?.errorRows) ? opResult.ui.errorRows : [],
        detailsJson: opResult?.ui?.detailsJson ?? opResult?.data ?? null,
    };
};


const visualizzaErogatoRichiesta = {
    index: 0,
    pinCode: "",
    codiceRegioneErogatore: "",
    codiceAslErogatore: "",
    codiceSsaErogatore: "",
    pwd: "",
    nre: "",
    cfAssistito: "",
    tipoOperazione: "",
};

export const richiestaNRE = {
    endPoint: "",
    user: "",
    password: "",
    richiesta: visualizzaErogatoRichiesta,
    rilascia: false
};

export const gestisciQuantita = (list = [], esami = []) => {
    const groups = new Map();

    // raggruppo per codProdPrest e normalizzo la quantità su "quantita"
    for (const item of list) {
        const key = String(item?.codProdPrest ?? "");
        if (!key) continue;

        const qty = Number(item?.quantita ?? item?.qta ?? item?.quantitaPrescr ?? 1) || 1;
        const normalized = { ...item, quantita: qty };

        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(normalized);
    }

    const result = [];

    for (const [key, items] of groups) {
        // quante possibili associazioni ho in "esami" per questo codProdPrest?
        const matches = esami.filter(e => String(e?.codiceMinisteriale) === key);
        const hasMultipleAssociations = matches.length > 1;

        if (items.length > 1 && !hasMultipleAssociations) {
            // caso: più prescrizioni ma una sola (o zero) associazione possibile -> UNISCO
            const first = items[0];
            const totalQty = items.reduce((sum, it) => sum + (Number(it?.quantita) || 1), 0);
            result.push({ ...first, quantita: totalQty });
        } else {
            // caso: una sola prescrizione oppure più associazioni possibili -> NON UNISCO
            // mantengo l'ordine originale
            result.push(...items);
        }
    }

    return result;
};

const PRIORITY_DAYS = {
    U: 3,    // Urgente (72h)
    B: 10,   // Breve
    D: 60,   // Differibile (spesso 30, a volte 60 per diagnostica)
    P: 120,  // Programmabile
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const parseDateAny = (v) => {
    if (!v) return null;
    if (v instanceof Date && !isNaN(v)) return v;

    const s = String(v).trim();
    if (!s) return null;

    // yyyyMMdd (es. 20260112)
    if (/^\d{8}$/.test(s)) {
        const y = Number(s.slice(0, 4));
        const m = Number(s.slice(4, 6)) - 1;
        const d = Number(s.slice(6, 8));
        const dt = new Date(y, m, d);
        return isNaN(dt) ? null : dt;
    }

    // ISO / yyyy-MM-dd / ecc.
    const dt = new Date(s);
    return isNaN(dt) ? null : dt;
};

const diffDays = (fromDate, toDate) => {
    // differenza intera in giorni (to - from)
    const ms = startOfDay(toDate).getTime() - startOfDay(fromDate).getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
};

export const calcolaFuoriPriorita = (dataPrenotazioneRaw, classePrioritaRaw, referenceDate = new Date()) => {
    const dataPrenotazione = parseDateAny(dataPrenotazioneRaw);
    const classe = String(classePrioritaRaw ?? "").trim().toUpperCase();

    const giorniConsentiti = PRIORITY_DAYS[classe] ?? null;
    if (!dataPrenotazione || !giorniConsentiti) {
        return {
            ok: false,
            giorniFuori: 0,
            giorniConsentiti: giorniConsentiti ?? 0,
            scadenza: null,
            giorniTrascorsi: 0,
            giorniResidui: 0,
            classe,
        };
    }

    const scadenza = new Date(dataPrenotazione);
    scadenza.setDate(scadenza.getDate() + giorniConsentiti);

    const giorniTrascorsi = diffDays(dataPrenotazione, referenceDate);
    const giorniResidui = diffDays(referenceDate, scadenza); // positivo se c'è ancora tempo
    const giorniFuori = Math.max(0, diffDays(scadenza, referenceDate)); // positivo solo se oltre scadenza

    return {
        ok: true,
        giorniFuori,
        giorniConsentiti,
        scadenza,
        giorniTrascorsi,
        giorniResidui: Math.max(0, giorniResidui),
        classe,
    };
};


export const renderPrioritaAlert = (info) => {
    if (!info?.ok) {
        return (
            <div className="alert alert-secondary d-flex justify-content-between align-items-center mb-0">
                <div>
                    <div className="fw-semibold">Priorità non calcolabile</div>
                    <div className="small">
                        Manca la <b>data prenotazione</b> oppure la <b>classe priorità</b> (U/B/D/P).
                    </div>
                </div>
                <div className="badge bg-dark">N/D</div>
            </div>
        );
    }

    const scad = info.scadenza ? formattaData(info.scadenza) : "-";
    const cls = info.classe || "-";
    const max = info.giorniConsentiti;

    if (info.giorniFuori > 0) {
        return (
            <div className="alert alert-danger d-flex justify-content-between align-items-center mb-0">
                <div>
                    <div className="fw-semibold">
                        Fuori priorità di <u>{info.giorniFuori}</u> giorni
                    </div>
                    <div className="small">
                        Classe <b>{cls}</b>: tempo massimo <b>{max}</b> giorni. Scadenza prevista: <b>{scad}</b>.
                    </div>
                </div>
                <div className="badge bg-danger">{`+${info.giorniFuori} gg`}</div>
            </div>
        );
    }

    return (
        <div className="alert alert-success d-flex justify-content-between align-items-center mb-0">
            <div>
                <div className="fw-semibold">Nei tempi della priorità</div>
                <div className="small">
                    Classe <b>{cls}</b>: massimo <b>{max}</b> giorni. Scadenza: <b>{scad}</b>. Giorni residui:{" "}
                    <b>{info.giorniResidui}</b>.
                </div>
            </div>
            <div className="badge bg-success">{`${info.giorniResidui} gg`}</div>
        </div>
    );
};


